import { useCallback, useEffect, useMemo } from 'react';

type KeyboardEventElement = Document | HTMLElement;

type EventListener = (
  type: 'keydown',
  listener: (
    this: KeyboardEventElement,
    ev: React.KeyboardEvent,
    /* This 'any' type is OK, since this type is just pulled from React's internals and collapsed
       over the union KeyboardEventElement. */
    /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
  ) => any,
) => void;

/**
 * A typeguard that ensures that the provided event has a 'key' property that is a string.
 *
 * Occasionally, for reasons unclear at the time of this writing, a bug occurs where even though
 * the event listener is only listening for `keydown` events, which should only ever call the
 * listener with an event of type {@link React.KeyboardEvent} (where the 'key' is defined), the
 * event had an undefined key.  This typeguard exists for purposes of avoiding those edge cases,
 * until further investigation can be done.
 */
const isKeyboardEvent = (event: unknown): event is React.KeyboardEvent =>
  typeof event === 'object' &&
  event !== null &&
  !Array.isArray(event) &&
  'key' in event &&
  typeof event.key === 'string';

/**
 * Represents global flags for disabling/enabling the invocation of all methods associated with the
 * provided event configurations, {@link KeyboardEventConfig}, when a {@link KeyboardEvent} is
 * detected.
 */
type GlobalKeyboardEventConfig = {
  readonly isDisabled?: boolean;
  readonly isEnabled?: boolean;
};

type KeyboardEventKeyConfig = {
  /**
   * The method that should be invoked when a keyboard event, {@link React.KeyboardEvent}, is
   * detected and the event is associated with the 'key' of the event configuration,
   * {@link KeyboardEventKeyConfig}.
   */
  readonly action?: (e: React.KeyboardEvent) => void;
  readonly conditional?: never;
  /**
   * Defines the key that will cause the 'action' associated with the event configuration,
   * {@link KeyboardEventKeyConfig}, to be invoked when detected on a keyboard event,
   * {@link KeyboardEvent}.
   */
  readonly key: string;
} & GlobalKeyboardEventConfig;

type KeyboardEventCallbackConfig = {
  /**
   * The method that should be invoked when a keyboard event, {@link React.KeyboardEvent}, is
   * detected and the 'conditional' associated with the event configuration,
   * {@link KeyboardEventCallbackConfig}, is met.
   */
  readonly action?: (e: React.KeyboardEvent) => void;
  /**
   * A method that returns a boolean value indicating whether or not the 'action' associated with
   * the event configuration, {@link KeyboardEventCallbackConfig}, should be invoked for a given
   * keyboard event, {@link React.KeyboardEvent}.
   */
  readonly conditional: (e: React.KeyboardEvent) => boolean;
  readonly key?: never;
} & GlobalKeyboardEventConfig;

/**
 * Represents the configuration properties that are used by the
 * {@link useKeyboardEvents} hook to conditionally invoke methods depending
 * on the event, {@link KeyboardEvent}, that was detected.
 */
export type KeyboardEventConfig = KeyboardEventCallbackConfig | KeyboardEventKeyConfig;

const _parseArgs = (
  config: KeyboardEventConfig | KeyboardEventConfig[],
  arg1?: GlobalKeyboardEventConfig | KeyboardEventElement,
  arg2?: KeyboardEventElement,
): {
  config: KeyboardEventConfig[];
  element: KeyboardEventElement;
  globalConfig?: GlobalKeyboardEventConfig;
} => {
  if (arg1 instanceof HTMLElement || arg1 instanceof Document) {
    return {
      config: Array.isArray(config) ? config : [config],
      element: arg1,
    };
  }
  return {
    config: Array.isArray(config) ? config : [config],
    element: arg2 ?? document,
    globalConfig: arg1,
  };
};

export function useKeyboardEvents(
  config: KeyboardEventConfig[],
  /* The global configuration is only applicable if multiple configurations are provided. */
  globalConfig?: GlobalKeyboardEventConfig,
  element?: KeyboardEventElement,
): void;

export function useKeyboardEvents(
  config: KeyboardEventConfig,
  element?: KeyboardEventElement,
): void;

export function useKeyboardEvents(
  config: KeyboardEventConfig | KeyboardEventConfig[],
  element: KeyboardEventElement,
): void;

/**
 * A hook that can be used to respond to keyboard events, {@link KeyboardEvent}, in a configurable
 * manner.  This hook is particularly useful for the development of reusable components that should
 * have configurable keyboard interactions.
 *
 * @example
 * // Using 'useKeyboardEvents' with a single configuration and custom element.
 * const element = document.getElementById('my-element');
 * useKeyboardEvents({
 *   key: "Enter",
 *   action: () => props.onClear(),
 *   isDisabled: props.keyboardEventsDisabled
 * }, element )
 * @example
 * // Using 'useKeyboardEvents' with multiple configurations, global enable/disable flags and a
 * // custom element.
 * const element = document.getElementById('my-element');
 * useKeyboardEvents(
 *   [
 *     { key: "Enter", action: props.onSubmit },
 *     { key: "Escape", action: props.onCancel }
 *   ],
 *   { isDisabled: props.keyboardEventsDisabled }
 *  element
 * )
 */
export function useKeyboardEvents(
  config: KeyboardEventConfig | KeyboardEventConfig[],
  arg1?: GlobalKeyboardEventConfig | KeyboardEventElement,
  arg2?: KeyboardEventElement,
) {
  const {
    config: configs,
    element,
    globalConfig,
  } = useMemo(() => _parseArgs(config, arg1, arg2), [config, arg1, arg2]);

  const configIsEnabled = useCallback(
    ({ isDisabled: isLocallyDisabled, isEnabled: isLocallyEnabled }: KeyboardEventConfig) => {
      const isEnabled = isLocallyEnabled ?? globalConfig?.isEnabled;
      const isDisabled = isLocallyDisabled ?? globalConfig?.isDisabled;
      return isEnabled !== false && isDisabled !== true;
    },
    [globalConfig],
  );

  useEffect(() => {
    const listener = (e: unknown) => {
      if (isKeyboardEvent(e)) {
        for (const { action, conditional, key } of configs.filter(c => configIsEnabled(c))) {
          const shouldTrigger =
            key === undefined ? conditional(e) : e.key.toLowerCase() === key.toLowerCase();
          if (shouldTrigger) {
            action?.(e);
          }
        }
      }
    };
    /* This type coercion is necessary because the function signatures for addEventListener and
       removeEventListener are defined with different 'this' parameters, which does not distribute
       over the union type 'KeyboardEventElement' - but, the coercions are safe because the coerced
       type is effectively the same thing as the original. */
    const addEventListener = element.addEventListener as EventListener;
    addEventListener('keydown', listener);
    return () => {
      const removeEventListener = element.removeEventListener as EventListener;
      removeEventListener('keydown', listener);
    };
  }, [configIsEnabled, configs, element]);
}
